// res/components/CarouselWeb.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
  ViewStyle,
  View,
} from "react-native";

export interface CarouselHandle {
  scrollTo: (index: number, animated?: boolean) => void;
  next: () => void;
  prev: () => void;
  getCurrentIndex: () => number;
}

interface CarouselProps<T> {
  data: T[];
  renderItem: ({ item, index }: { item: T; index: number }) => React.ReactNode;
  width: number;
  height: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  loop?: boolean;
  pagingEnabled?: boolean;
  style?: ViewStyle;
}

const CarouselInner = <T,>(
  {
    data,
    renderItem,
    width,
    height,
    autoPlay = false,
    autoPlayInterval = 3500,
    loop = false,
    pagingEnabled = true,
    style,
  }: CarouselProps<T>,
  ref: React.Ref<CarouselHandle>,
) => {
  const scrollRef = useRef<ScrollView>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paused = useRef(false);
  const [index, setIndex] = useState(0);

  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const scrollTo = (i: number, animated = true) => {
    scrollRef.current?.scrollTo({ x: width * i, animated });
  };

  const next = () => {
    setIndex((prev) => {
      const nxt = prev + 1;
      return nxt >= data.length ? (loop ? 0 : prev) : nxt;
    });
  };

  const prev = () => {
    setIndex((prev) => {
      const p = prev - 1;
      return p < 0 ? (loop ? data.length - 1 : prev) : p;
    });
  };

  useImperativeHandle(ref, () => ({
    scrollTo,
    next,
    prev,
    getCurrentIndex: () => index,
  }));

  const start = () => {
    if (!autoPlay || paused.current) return;
    clear();
    timer.current = setTimeout(next, autoPlayInterval);
  };

  useEffect(() => {
    scrollTo(index);
    start();
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, autoPlay, autoPlayInterval, loop, data.length]);

  const onScrollBeginDrag = () => {
    if (!autoPlay) return;
    paused.current = true;
    clear();
  };

  const onScrollEndDrag = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const newIdx = Math.round(e.nativeEvent.contentOffset.x / width);
    paused.current = false;
    setIndex(newIdx);
  };

  // Separate layout props that react-native-web requires on contentContainerStyle
  const layoutKeys = new Set(['justifyContent', 'alignItems', 'alignContent', 'flexDirection', 'flexWrap']);
  const containerStyle: ViewStyle = {};
  const outerStyle: ViewStyle = {};
  if (style) {
    for (const [key, value] of Object.entries(style)) {
      if (layoutKeys.has(key)) {
        (containerStyle as any)[key] = value;
      } else {
        (outerStyle as any)[key] = value;
      }
    }
  }

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      pagingEnabled={pagingEnabled}
      showsHorizontalScrollIndicator={false}
      style={[{ width, height }, outerStyle]}
      contentContainerStyle={containerStyle}
      scrollEventThrottle={16}
      onScrollBeginDrag={onScrollBeginDrag}
      onScrollEndDrag={onScrollEndDrag}
    >
      {data.map((item, i) => (
        <View key={i} style={{ width, height }}>
          {renderItem({ item, index: i })}
        </View>
      ))}
    </ScrollView>
  );
};

const Carousel = forwardRef(CarouselInner) as <T>(
  p: CarouselProps<T> & { ref?: React.Ref<CarouselHandle> }
) => ReturnType<typeof CarouselInner>;

export default Carousel;
